'use client';

import { useEffect, useRef, useState } from 'react';
import { store } from '@/store';
import { checkoutRevealStore } from '@/features/subscription/utils/checkout/checkoutRevealStore';
import { checkoutVisibilityStore } from '@/features/subscription/utils/checkout/checkoutVisibilityStore';

const DEFAULT_WIDTH = 560;
const DEFAULT_HEIGHT = 450;

const getDocumentOffset = (element: HTMLElement) => {
  let top = 0;
  let left = 0;
  let node: HTMLElement | null = element;

  while (node) {
    top += node.offsetTop;
    left += node.offsetLeft;
    const parent = node.offsetParent as HTMLElement | null;
    if (parent) {
      top += parent.clientTop;
      left += parent.clientLeft;
    }
    node = parent;
  }

  return { top, left };
};

const PersistentCheckoutHost = () => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const revealedRef = useRef(false);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    let rafId: number | null = null;
    let lastInert: boolean | null = null;
    let lastKey: string | null = null;
    let scrollAncestor: Element | null = null;
    let scrollAncestorFor: Element | null = null;

    const findScrollAncestor = (el: Element): Element | null => {
      let node = el.parentElement;
      while (node && node !== document.body) {
        const overflowY = window.getComputedStyle(node).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
          return node;
        }
        node = node.parentElement;
      }
      return null;
    };

    const syncPosition = (): { host: HTMLDivElement; target: HTMLElement; rect: DOMRect } | null => {
      const host = hostRef.current;
      const target = checkoutRevealStore.getRevealTarget();
      if (!host || !target) return null;

      if (scrollAncestorFor !== target) {
        scrollAncestor = findScrollAncestor(target);
        scrollAncestorFor = target;
      }

      const frameHeight = host.querySelector('iframe')?.offsetHeight ?? 0;
      if (
        frameHeight > 0 &&
        frameHeight !== checkoutRevealStore.getContentHeight()
      ) {
        checkoutRevealStore.setContentHeight(frameHeight);
      }

      const followsDocumentScroll = scrollAncestor === null;
      const rect = target.getBoundingClientRect();
      const documentOffset = followsDocumentScroll
        ? getDocumentOffset(target)
        : null;
      const roundedLeft = Math.round(documentOffset?.left ?? rect.left);
      const roundedTop = Math.round(documentOffset?.top ?? rect.top);
      const roundedWidth = Math.round(rect.width || DEFAULT_WIDTH);
      const roundedHeight = Math.max(
        Math.round(rect.height || DEFAULT_HEIGHT),
        Math.round(checkoutRevealStore.getContentHeight() ?? 0)
      );
      const key = `${followsDocumentScroll}|${roundedLeft}|${roundedTop}|${roundedWidth}|${roundedHeight}`;

      if (key !== lastKey) {
        lastKey = key;
        host.style.position = followsDocumentScroll ? 'absolute' : 'fixed';
        host.style.transform = `translate(${roundedLeft}px, ${roundedTop}px)`;
        host.style.width = `${roundedWidth}px`;
        host.style.height = `${roundedHeight}px`;

        if (scrollAncestor) {
          const ancestorRect = scrollAncestor.getBoundingClientRect();
          const clipTop = Math.max(0, ancestorRect.top - rect.top);
          const clipLeft = Math.max(0, ancestorRect.left - rect.left);
          const clipRight = Math.max(0, rect.left + rect.width - (ancestorRect.left + ancestorRect.width));
          const clipBottom = Math.max(0, rect.top + rect.height - (ancestorRect.top + ancestorRect.height));
          host.style.clipPath = `inset(${clipTop}px ${clipRight}px ${clipBottom}px ${clipLeft}px)`;
        } else {
          host.style.clipPath = '';
        }
      }

      return { host, target: target as HTMLElement, rect };
    };

    const onWindowResize = () => {
      syncPosition();
    };
    window.addEventListener('resize', onWindowResize);

    const tick = () => {
      const synced = syncPosition();

      if (synced) {
        const { host, rect } = synced;
        const nextInert = checkoutRevealStore.getInert();

        if (nextInert !== lastInert) {
          lastInert = nextInert;
          host.inert = nextInert;
        }

        const currentPaymentStatus = store.getState().payment.status;
        const isReady =
          rect.width > 0 &&
          rect.height > 0 &&
          checkoutVisibilityStore.getVisibleTransactionId() != null &&
          (currentPaymentStatus === 'checkout_ready' ||
            currentPaymentStatus === 'processing' ||
            currentPaymentStatus === 'completed');

        if (isReady !== revealedRef.current) {
          revealedRef.current = isReady;
          setRevealed(isReady);
        }
      }

      rafId = requestAnimationFrame(tick);
    };

    const syncLoop = () => {
      const target = checkoutRevealStore.getRevealTarget();

      if (target && rafId == null) {
        rafId = requestAnimationFrame(tick);
      } else if (!target && rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
        revealedRef.current = false;
        lastKey = null;
        lastInert = null;
        scrollAncestor = null;
        scrollAncestorFor = null;
        if (hostRef.current) {
          hostRef.current.style.position = 'fixed';
        }
        setRevealed(false);
      }
    };

    const unsubscribe = checkoutRevealStore.subscribe(syncLoop);
    syncLoop();

    return () => {
      unsubscribe();
      window.removeEventListener('resize', onWindowResize);
      if (rafId != null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  return (
    <div
      ref={hostRef}
      className="checkout-container"
      data-react-aria-top-layer="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: DEFAULT_WIDTH,
        height: DEFAULT_HEIGHT,
        opacity: revealed ? 1 : 0,
        pointerEvents: revealed ? 'auto' : 'none',
        transition: 'opacity 120ms ease-out',
        willChange: 'transform',
        zIndex: 9999,
        overflow: 'hidden',
      }}
    />
  );
};

export default PersistentCheckoutHost;
